import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ApplyPlan, PlannedChange, ProposalLineState } from '../vendor-quote-flow.types';
import { changeLabel, lineDiff, simulate, sortedChanges, totals } from '../vendor-quote-flow.apply';

export interface CompareVendorQuoteChangesDialogData {
  plan: ApplyPlan;
  /** The scope's lines as they are now. */
  lines: ProposalLineState[];
  /** Where Compare is opened from. 'purchase-order' words it for a PO and drops the profit line. Defaults to 'proposal'. */
  context?: 'proposal' | 'purchase-order';
  /**
   * The whole quote applied, offered beside `plan` (which then covers one item only) so the user
   * can choose "Only this item" or "Complete quote". With its own before-lines.
   */
  fullPlan?: ApplyPlan;
  fullLines?: ProposalLineState[];
  /** Which of the two to open on, when coming Back to Compare. */
  scope?: 'item' | 'quote';
}

export interface CompareVendorQuoteChangesDialogResult {
  action: 'apply' | 'back';
  plan: ApplyPlan;
  /** Which plan was applied: the one item, or the complete quote (only when fullPlan was given). */
  scope: 'item' | 'quote';
}

/** Compare before you apply (mockup H1–H4). Keep / Remove for unquoted lines is written back into the plan. */
@Component({
  selector: 'app-compare-vendor-quote-changes-dialog',
  templateUrl: './compare-vendor-quote-changes-dialog.component.html',
  styleUrls: ['./compare-vendor-quote-changes-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class CompareVendorQuoteChangesDialogComponent {
  plan: ApplyPlan;
  lines: ProposalLineState[];
  rows: PlannedChange[];
  before: { cost: number; sell: number };
  changeLabel = changeLabel;
  lineDiff = lineDiff;
  readonly isPo: boolean;
  /** Set when "Complete quote" is offered beside the single item. */
  readonly fullPlan?: ApplyPlan;
  scope: 'item' | 'quote' = 'item';

  constructor(
    private dialogRef: MatDialogRef<CompareVendorQuoteChangesDialogComponent, CompareVendorQuoteChangesDialogResult | undefined>,
    @Inject(MAT_DIALOG_DATA) private data: CompareVendorQuoteChangesDialogData,
    private cd: ChangeDetectorRef,
  ) {
    this.isPo = data.context === 'purchase-order';
    this.fullPlan = data.fullPlan;
    this.plan = data.plan;
    this.lines = data.lines;
    this.rows = sortedChanges(this.plan);
    this.before = totals(this.lines);
    if (data.scope === 'quote' && data.fullPlan) this.setScope('quote');
  }

  /** "Only this item" or "Complete quote": swaps the plan, its rows and the totals it is measured against. */
  setScope(scope: 'item' | 'quote'): void {
    if (scope === 'quote' && !this.data.fullPlan) return;
    this.scope = scope;
    this.plan = scope === 'quote' ? this.data.fullPlan! : this.data.plan;
    this.lines = scope === 'quote' ? this.data.fullLines ?? this.data.lines : this.data.lines;
    this.rows = sortedChanges(this.plan);
    this.before = totals(this.lines);
    this.cd.markForCheck();
  }

  /** How many of the complete quote's items change or add a line (for the "Complete quote" card). */
  get fullChangeCount(): number {
    return this.fullPlan?.changes.filter(c => c.kind === 'changed' || c.kind === 'added').length ?? 0;
  }

  /** Totals after the plan, following the Keep / Remove choices (H1). */
  get after(): { cost: number; sell: number } {
    return totals(simulate(this.lines, this.plan));
  }

  get costDiff(): number {
    return this.after.cost - this.before.cost;
  }

  get sellDiff(): number {
    return this.after.sell - this.before.sell;
  }

  setRemove(c: PlannedChange, remove: boolean): void {
    c.remove = remove;
    this.cd.markForCheck();
  }

  changed(c: PlannedChange, field: 'qty' | 'unitCost' | 'vendorName'): boolean {
    return !!c.before && !!c.after && c.before[field] !== c.after[field];
  }

  absolute(n: number): number {
    return Math.abs(n);
  }

  renamed(c: PlannedChange): boolean {
    return !!c.before && !!c.after && c.before.name !== c.after.name;
  }

  trackByRow(index: number, c: PlannedChange): string {
    return `${c.kind}:${c.before?.id ?? 'n'}:${c.after?.productId ?? 'n'}`;
  }

  cancel(): void {
    this.dialogRef.close(undefined);
  }

  back(): void {
    this.dialogRef.close({ action: 'back', plan: this.plan, scope: this.scope });
  }

  apply(): void {
    this.dialogRef.close({ action: 'apply', plan: this.plan, scope: this.scope });
  }
}
